import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon, statusMeta } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

function getLocation(timeout = 4000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: +p.coords.latitude.toFixed(6), lng: +p.coords.longitude.toFixed(6) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout, maximumAge: 60000 }
    );
  });
}

export default function PublicScan() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const loc = await getLocation();
    setCoords(loc);
    try {
      const q = loc ? `?lat=${loc.lat}&lng=${loc.lng}` : "";
      const res = await api.get(`/public/t/${token}${q}`);
      setData(res.data.data);
    } catch {
      setData({ status: "INVALID", status_label: "Invalid", message: "This QR code is invalid." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-zinc-100"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const meta = statusMeta(data.status);
  const theme = data.theme || {};
  const isLost = data.status === "LOST";
  // Whole-page theme: heroColor drives the big band, surface tints the page.
  const heroColor = isLost ? "#FF3B30" : (theme.accent || meta.color);
  const surface = theme.surface || "#F4F4F5";
  const btn = isLost ? "#FF3B30" : (theme.accent || "#0A0A0A");

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${surface} 0%, #ffffff 60%)` }}>
      <div className="scan-frame bg-white flex flex-col" style={{ boxShadow: `0 24px 60px -20px ${heroColor}55` }}>
        {/* Themed status hero */}
        <div className="px-6 pt-14 pb-12 text-center text-white relative overflow-hidden"
             style={{ background: `linear-gradient(155deg, ${heroColor} 0%, ${shade(heroColor, -18)} 100%)` }}>
          <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }} />
          <div className="absolute -bottom-20 -left-10 w-48 h-48 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
          <div className="relative">
            <div className="w-24 h-24 mx-auto rounded-[1.75rem] bg-white/15 backdrop-blur grid place-items-center animate-rise ring-1 ring-white/20">
              <Icon name={data.category?.icon || meta.icon} className="w-12 h-12" />
            </div>
            <p className="mt-5 text-xs font-bold uppercase tracking-[0.22em] text-white/80">
              {data.category?.name || "QR Tag"}{data.theme?.name ? ` · ${data.theme.name}` : ""}
            </p>
            <h1 className="font-display font-black text-3xl mt-2 leading-tight animate-rise">{headline(data)}</h1>
            {data.tag_number && <p className="text-white/70 text-sm mt-3 font-mono">{data.tag_number}</p>}
          </div>
        </div>

        <div className="flex-1 px-6 py-8 space-y-5">
          {["INVALID", "REVOKED", "DISABLED"].includes(data.status) && (
            <p className="text-center text-muted-foreground text-lg">{data.message}</p>
          )}

          {data.claimable && (
            <div className="space-y-5">
              <p className="text-center text-muted-foreground text-lg">This tag is ready to be activated.</p>
              <button data-testid="scan-claim-btn" onClick={() => nav(user ? `/claim?token=${token}` : `/login?next=/claim?token=${token}`)}
                className="tap w-full h-16 rounded-full text-white font-bold text-lg flex items-center justify-center gap-2" style={{ backgroundColor: btn }}>
                Claim this tag <Icon name="ArrowRight" className="w-5 h-5" />
              </button>
              <p className="text-center text-xs text-muted-foreground">You'll need the authentication code from your tag's package.</p>
            </div>
          )}

          {(data.status === "ACTIVE" || data.status === "LOST") && (
            <ActiveBody data={data} token={token} coords={coords} btn={btn} onSent={load} />
          )}
        </div>

        <div className="px-6 py-5 border-t border-border flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="ShieldCheck" className="w-3.5 h-3.5" /> Secured by <Link to="/" className="font-bold text-foreground">TagBack</Link>
        </div>
      </div>
    </div>
  );
}

function headline(d) {
  switch (d.status) {
    case "LOST": return "This item is reported LOST";
    case "ACTIVE": case "CLAIMED": case "FOUND": return "This item has an owner";
    case "REVOKED": return "Tag no longer valid";
    case "DISABLED": return "Tag temporarily inactive";
    case "INVALID": return "Invalid QR code";
    default: return "Activate this tag";
  }
}

function shade(hex, percent) {
  try {
    const n = parseInt(hex.replace("#", ""), 16);
    let r = (n >> 16) + Math.round(2.55 * percent);
    let g = ((n >> 8) & 0xff) + Math.round(2.55 * percent);
    let b = (n & 0xff) + Math.round(2.55 * percent);
    r = Math.max(0, Math.min(255, r)); g = Math.max(0, Math.min(255, g)); b = Math.max(0, Math.min(255, b));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch { return hex; }
}

function ActiveBody({ data, token, coords, btn, onSent }) {
  const isLost = data.status === "LOST";
  return (
    <div className="space-y-5">
      {data.owner_name && (
        <div className="flex items-center gap-3 justify-center text-muted-foreground">
          <Icon name="User" className="w-4 h-4" />
          <span>Owner: <span className="font-semibold text-foreground">{data.owner_name}</span></span>
        </div>
      )}
      {(data.lost_message || data.public_message) && (
        <div className="rounded-2xl p-5 text-center border" style={{ backgroundColor: isLost ? "#FFECEA" : (data.theme?.surface || "#F4F4F5"), borderColor: isLost ? "#FF3B3033" : "transparent" }}>
          <p className="italic text-foreground">"{data.lost_message || data.public_message}"</p>
        </div>
      )}

      {data.phone && (
        <div className="rounded-2xl border border-border p-5 text-center">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Owner contact</p>
          <p className="font-display font-bold text-2xl mt-1">{data.phone}</p>
          <div className="flex gap-3 mt-4">
            {data.allow_finder_call && (
              <a data-testid="scan-call-btn" href={`tel:${data.phone.replace(/\s/g, "")}`} className="tap flex-1 h-14 rounded-full text-white font-bold flex items-center justify-center gap-2" style={{ backgroundColor: btn }}>
                <Icon name="Phone" className="w-4 h-4" /> Call
              </a>
            )}
            {data.allow_whatsapp_contact && (
              <a data-testid="scan-whatsapp-btn" href={`https://wa.me/${data.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="tap flex-1 h-14 rounded-full bg-[#25D366] text-white font-bold flex items-center justify-center gap-2">
                <Icon name="MessageCircle" className="w-4 h-4" /> WhatsApp
              </a>
            )}
          </div>
        </div>
      )}

      {data.allow_finder_message && <ContactDialog token={token} coords={coords} btn={btn} onSent={onSent} isLost={isLost} />}

      {!data.phone && !data.allow_finder_message && (
        <p className="text-center text-muted-foreground text-sm">The owner has not enabled contact for this tag.</p>
      )}
    </div>
  );
}

function ContactDialog({ token, coords, btn, onSent, isLost }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ finder_name: "", finder_phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [shareLoc, setShareLoc] = useState(true);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let loc = coords;
      if (shareLoc && !loc) loc = await getLocation(6000);
      const payload = { ...form };
      if (shareLoc && loc) { payload.lat = loc.lat; payload.lng = loc.lng; }
      await api.post(`/public/t/${token}/contact`, payload);
      toast.success("Message delivered to the owner!");
      setOpen(false);
      setForm({ finder_name: "", finder_phone: "", message: "" });
      onSent?.();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="scan-contact-btn" className="tap w-full h-16 rounded-full text-white font-bold text-lg flex items-center justify-center gap-2" style={{ backgroundColor: btn }}>
          <Icon name="Send" className="w-5 h-5" /> Contact Owner
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-[420px]">
        <DialogHeader><DialogTitle className="font-display text-2xl">Contact the owner</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Your message is securely forwarded. The owner's number stays private unless they share it.</p>
        <form onSubmit={send} className="space-y-3 mt-2" data-testid="contact-form">
          <input data-testid="finder-name" required placeholder="Your name" value={form.finder_name} onChange={set("finder_name")} className="w-full py-3.5 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
          <input data-testid="finder-phone" required placeholder="Your mobile number" value={form.finder_phone} onChange={set("finder_phone")} className="w-full py-3.5 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
          <textarea data-testid="finder-message" required placeholder={isLost ? "I found your item near..." : "Message to the owner"} value={form.message} onChange={set("message")} rows={3} maxLength={1000} className="w-full py-3.5 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
          <label className="flex items-center gap-2.5 text-sm rounded-xl border border-border p-3 cursor-pointer" data-testid="share-location-toggle">
            <input type="checkbox" checked={shareLoc} onChange={(e) => setShareLoc(e.target.checked)} className="w-4 h-4" />
            <span className="flex items-center gap-1.5"><Icon name="MapPin" className="w-4 h-4" /> Share my current location with the owner</span>
          </label>
          <button data-testid="finder-send" disabled={busy} className="tap w-full h-14 rounded-full text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: btn }}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send message <Icon name="Send" className="w-4 h-4" /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
